import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { t } from 'ember-intl'
import { not } from 'ember-truth-helpers'
import { service } from '@ember/service'
import type MathseedsPrimeDashboardTableRoute from 'teacher-ui-client/reporting/ui-scope/mathseeds-prime/dashboard-table/route'
import type MathseedsPrimeDashboardTableController from 'teacher-ui-client/reporting/ui-scope/mathseeds-prime/dashboard-table/controller'
import { ReportingControls } from 'teacher-ui-client/components/reporting/controls'
import { ReportingControlsGap } from 'teacher-ui-client/components/reporting/controls-gap'
import { ReportingUiScopeSelect } from 'teacher-ui-client/components/reporting/ui-scope-select'
import { ReportingPeriodSelect } from 'teacher-ui-client/components/reporting/period-select'
import { ReportingExportSelect, ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { StudentSearchComponent } from 'teacher-ui-client/components/student-search'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import type { ReportData } from 'teacher-ui-client/components/reporting/mathseeds-prime/dashboard'
import type { ReportingQuerySort, RequestScope } from 'teacher-ui-client/services/reporting'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import type ReportingService from 'teacher-ui-client/services/reporting'
import type SessionService from 'teacher-ui-client/services/session'
import type { IntlService } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type Features from 'teacher-ui-client/services/features'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { Product } from 'teacher-ui-client/utils/products'
import { task, timeout } from 'ember-concurrency'
import { roundAccurately } from 'teacher-ui-client/utils/round'
import type FileDownload from 'teacher-ui-client/services/file-download'
import type { NormalizedInterval } from 'date-fns'
import { MathseedsPrimeDashboard } from 'teacher-ui-client/components/reporting/mathseeds-prime/dashboard'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'

interface Signature {
  Args: {
    model: ModelFor<MathseedsPrimeDashboardTableRoute>
    controller: MathseedsPrimeDashboardTableController
  }
}

export class MathseedsPrimeDashboardTablePage extends Component<Signature> {
  @service intl!: IntlService

  @service reporting!: ReportingService

  @service session!: SessionService

  @service gradeSets!: GradeSetsService

  @service fileDownload!: FileDownload

  @service features!: Features

  @service flashQueue!: FlashQueueService

  get exportOptions() {
    return [{ exportType: ExportType.CSV, exportAction: () => this.exportCsv() }]
  }

  fetchStatsForExport = task(
    { restartable: true },
    async (sort: ReportingQuerySort, period: NormalizedInterval, requestScope: RequestScope) => {
      await timeout(500)

      const url = this.reporting.buildGravityUrl({
        product: Product.MSP,
        requestScope,
        reportName: 'dashboard/statistics/students-table',
      })

      const query = this.reporting.buildQuery({ sort, page: 'all', period })

      return this.reporting.fetchReport<ReportData>(url, query)
    },
  )

  async exportCsv() {
    const subscriptionType = SubscriptionType.Maths
    const { uiScope, period } = this.args.model
    const sort = this.args.controller.sort
    const requestScope = toRequestScope(uiScope)

    const isSubco = this.session.currentTeacher.subcoFor(subscriptionType)
    const isMeasurementEnabled = this.features.isEnabled('tui_ms_prime_measurement_thread')

    let report: Awaited<ReturnType<typeof this.fetchStatsForExport.perform>>
    try {
      report = await this.fetchStatsForExport.perform(sort, period, requestScope)
    } catch {
      this.flashQueue.addFail({ subtitle: this.intl.t('failedToExportReport') })
      return
    }

    const groupHeader = [
      [this.intl.t('student'), ...Array<string>(isSubco ? 5 : 3).fill('')],
      [this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.programTotals'), ''],
      this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.skillsMastered'),
      [
        this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.lessonsCompleted'),
        ...Array<string>(isMeasurementEnabled ? 4 : 3).fill(''),
      ],
      [this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.unitQuizzes'), '', ''],
    ].flat()

    const header = [
      this.intl.t('reporting.dashboardTables.firstName'),
      this.intl.t('reporting.dashboardTables.lastName'),
      this.intl.t('reporting.dashboardTables.studentExternalId'),
      this.intl.t('reporting.dashboardTables.classes'),
      ...(isSubco ? [this.intl.t('reporting.dashboardTables.teacher'), this.intl.t('grade.label')] : []),
      this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.totalLessonsCompleted'),
      this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.timeInProgram'),
      this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.totalLessonsPassed'),
      this.intl.t('mathseedsPrimeThreads.number.name'),
      this.intl.t('mathseedsPrimeThreads.fractions.name'),
      this.intl.t('mathseedsPrimeThreads.operations.longName'),
      this.intl.t('mathseedsPrimeThreads.geometry.name'),
      ...(isMeasurementEnabled ? [this.intl.t('mathseedsPrimeThreads.measurement.name')] : []),
      this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.numberOfQuizAttempts'),
      this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.averageNumberOfAttemptsToPass'),
      this.intl.t('components.reporting.tables.mathseedsPrime.dashboard.headers.averageQuizScore'),
    ]

    const aggregateData = report.aggregates
    const totals = [
      ...[this.intl.t('reporting.dashboardTables.totals'), ...Array<string>(isSubco ? 5 : 3).fill('')],
      aggregateData.lessons_completed,
      formatDuration(aggregateData.total_time),
      aggregateData.skills_mastered_total,
      aggregateData.number_lessons_completed,
      aggregateData.fractions_lessons_completed,
      aggregateData.operations_algebra_lessons_completed,
      aggregateData.geometry_lessons_completed,
      ...(isMeasurementEnabled ? [aggregateData.measurement_lessons_completed] : []),
      aggregateData.unit_quizzes_attempted,
    ]

    const rows = report.students.map((studentData) => {
      const gradeName = this.gradeSets.findByPosition(studentData.grade_position).name
      return [
        studentData.first_name,
        studentData.last_name,
        studentData.external_id,
        studentData.school_class_names.join(', '),
        ...(isSubco ? [studentData.teacher_names.join(', '), gradeName] : []),
        studentData.lessons_completed ?? '-',
        formatDuration(studentData.total_time, '-'),
        studentData.skills_mastered_total ?? '-',
        studentData.number_lessons_completed ?? '-',
        studentData.fractions_lessons_completed ?? '-',
        studentData.operations_algebra_lessons_completed ?? '-',
        studentData.geometry_lessons_completed ?? '-',
        ...(isMeasurementEnabled ? [studentData.measurement_lessons_completed ?? '-'] : []),
        studentData.unit_quizzes_attempted ?? '-',
        typeof studentData.unit_quizzes_average_attempts_to_pass === 'number'
          ? roundAccurately(studentData.unit_quizzes_average_attempts_to_pass, 1)
          : '-',
        typeof studentData.unit_quizzes_average_score === 'number'
          ? roundAccurately(studentData.unit_quizzes_average_score) + '%'
          : '-',
      ]
    })

    const filename = this.intl.t('reporting.mathseedsPrime.dashboard.exportCsvFilename', {
      start: this.intl.formatDate(period.start).replace(/\//g, '-'),
      end: this.intl.formatDate(period.end).replace(/\//g, '-'),
    })

    this.fileDownload.downloadAsCsv([groupHeader, header, totals, ...rows], { filename, header: true })
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "pages.reporting.mathseedsPrime.dashboard.title"}} />
    <ReportingControls>
      <ReportingUiScopeSelect class="shrink basis-80" />
      <ReportingControlsGap />
      <ReportingPeriodSelect />
      <ReportingControlsGap />
      <ReportingExportSelect @exportOptions={{this.exportOptions}} @disabled={{not @model.isDataPresent}} />
      <ReportingControlsGap />
      <StudentSearchComponent
        @studentRouteName="reporting.ui-scope.mathseeds-prime.dashboard-student"
        @students={{@model.scopedStudents}}
        @includeSelectAll={{false}}
      />
    </ReportingControls>

    <ContentTile>
      <MathseedsPrimeDashboard @period={{@model.period}} @uiScope={{@model.uiScope}} />
    </ContentTile>
  </template>
}

export default RouteTemplate(MathseedsPrimeDashboardTablePage)
